import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
import username from "flarum/common/helpers/username";
import humanTime from "flarum/common/helpers/humanTime";
import humanTimeUtil from "flarum/common/utils/humanTime";
import listItems from "flarum/common/helpers/listItems";
import classList from "flarum/common/utils/classList";
import { displayEmoji } from "../utils/emoji";
import { isOnline } from "../utils/presence";
import { authorAvatar, authorLink } from "../utils/bot";
import { safeFileUrl } from "../utils/url";
import { jumpToMessage } from "../utils/jumpToMessage";
import { verifiedBadge } from "../utils/integrations";
import FlagMessageModal from "./FlagMessageModal";
import ImageLightbox from "./ImageLightbox";
import { messagePreview } from "../../common/utils/preview";
import { refreshMessageCapabilities } from "../realtime";
/**
 * The one-click reaction on the hover bar.
 *
 * `+1` (👍) rather than a heart, so the quick reaction means the same thing here
 * as a Like does on a post — Flarum's own convention. Any other emoji is still
 * reachable through the picker, and hearts already stored on old messages keep
 * rendering as hearts.
 */
const LIKE_REACTION = "+1";

/**
 * One row in the message stream.
 *
 * Grouping, mention highlighting and tombstones are all decided from the model
 * rather than passed in, so a row rendered from a realtime push looks identical
 * to one rendered from a fetch.
 */
export default class ChatMessage extends Component {
  view() {
    var _message$pinnedAt;
    const _this$attrs = this.attrs,
      message = _this$attrs.message,
      previous = _this$attrs.previous,
      state = _this$attrs.state;

    // A pinned message never collapses into the run above it: grouping hides the
    // meta row, which is where the pin is stated.
    const grouped = message.isGroupedWith(previous) && !message.isPinned();
    const deleted = Boolean(message.isDeleted());
    const selected = state.selected.has(Number(message.id()));
    if (message.isSystem()) {
      return this.systemRow(message);
    }
    return m("div", {
      className: classList("ChatMessage", {
        "ChatMessage--grouped": grouped,
        "ChatMessage--pinned": !deleted && Boolean(message.isPinned()),
        "ChatMessage--mentioned": !deleted && message.mentionsActor(),
        "ChatMessage--selected": selected,
        // A bot post that stands for something elsewhere — currently only a new
        // discussion. The row stays an ordinary message: same avatar, same
        // timestamp, same reactions. Only its content block is drawn as a quoted
        // card, which is what distinguishes "here is a thing over there" from
        // somebody talking.
        "ChatMessage--announcement": !deleted && message.isBot() && Boolean(message.systemKey()),
        // Only meaningful on an ungrouped row, which is the one showing an
        // avatar for the halo to sit on.
        "ChatMessage--online": !grouped && isOnline(message.user() || null)
      }),
      "data-id": message.id(),
      onclick: state.selecting ? () => state.toggleSelected(Number(message.id())) : undefined
    }, m("div", {
      className: "ChatMessage-gutter"
    }, grouped ? this.shortTime(message) : this.avatar(message)), m("div", {
      className: "ChatMessage-body"
    }, grouped ? null : m("div", {
      className: "ChatMessage-meta"
    }, m("span", {
      className: "ChatMessage-author"
    }, authorLink(message)), message.isBot() ? null : verifiedBadge(message.user()), message.isBot() ? m("span", {
      className: "ChatMessage-botTag"
    }, app.translator.trans("ramon-chat.forum.bot.tag")) : null, message.createdAt() ? m("span", {
      className: "ChatMessage-time"
    }, humanTime(message.createdAt())) : null, !deleted && message.isPinned() ? m("span", {
      className: "ChatMessage-pinMark",
      title: String((_message$pinnedAt = message.pinnedAt()) != null ? _message$pinnedAt : "")
    }, m("i", {
      className: "fas fa-thumbtack",
      "aria-hidden": "true"
    }), app.translator.trans("ramon-chat.forum.message.pinned")) : null), this.replyPreview(message), deleted ? this.tombstone(message) : this.content(message), deleted ? null : this.uploads(message), deleted ? null : this.reactions(message), deleted ? null : this.threadIndicator(message)), state.selecting ? null : this.actions(message));
  }

  /**
   * Deleted rows keep their place in the stream. Removing them would silently
   * reflow the conversation and make replies to them incoherent.
   */
  tombstone(message) {
    // Decided by who removed it, never by whether the text was withheld. Those
    // are different questions: a message its author deleted is withheld from
    // everyone else as well, so keying off redaction announced every ordinary
    // self-deletion to the channel as a moderator removal.
    if (!message.isModeratorDeleted()) {
      return m("div", {
        className: "ChatMessage-tombstone"
      }, m("span", null, app.translator.trans("ramon-chat.forum.message.deleted")), this.purgeButton(message));
    }

    // `hasOne` yields false when the relationship was not included — a realtime
    // push carries no relations, so the unnamed wording is the fallback rather
    // than a separate case.
    const moderator = message.deletedBy() || null;

    // Naming them is the point: "removed by a moderator" leaves the author with
    // no idea who to ask.
    return m("div", {
      className: "ChatMessage-tombstone"
    }, m("span", null, moderator ? app.translator.trans("ramon-chat.forum.message.deleted_by_named", {
      username: username(moderator)
    }) : app.translator.trans("ramon-chat.forum.message.deleted_by_moderator")), this.purgeButton(message));
  }

  /**
   * The picture an announced discussion opened with, drawn at the head of the
   * card the way a link preview leads with a favicon.
   *
   * Only for bot announcements: an ordinary message showing its own attachments
   * already has the uploads row, and a second copy of the same image at the top
   * would be noise.
   *
   * The URL is filtered again here even though the server already accepted only
   * http(s). It lands in an `src`, the payload travels through JSON:API to get
   * here, and "the server checked" is a property of today's server.
   */
  announcementIcon(message) {
    if (!message.isBot() || !message.systemKey()) return null;
    const data = message.systemData();
    const url = safeFileUrl(data == null ? void 0 : data.image);
    if (!url) return null;
    return m("img", {
      className: "ChatMessage-announcementIcon",
      src: url,
      alt: "",
      loading: "lazy"
      // A picture that fails to load must not leave a broken-image glyph in
      // the middle of the card; removing the node is cleaner than styling it.
      ,
      onerror: e => {
        var _e$target;
        return (_e$target = e.target) == null ? void 0 : _e$target.remove();
      }
    });
  }

  /**
   * Removes the row for good.
   *
   * Offered on the tombstone rather than in the hover bar: a channel that has
   * collected tombstones is where this is wanted, and putting an irreversible
   * action next to "reply" and "react" is asking for a mis-click.
   *
   * The confirmation names what is lost, because "are you sure?" does not.
   */
  purgeButton(message) {
    if (!message.canForceDelete()) return null;
    return m(Button, {
      className: "Button Button--flat Button--icon ChatMessage-purge",
      icon: "fas fa-eraser",
      title: app.translator.trans("ramon-chat.forum.message.purge", {}, true),
      onclick: () => this.purge(message)
    });
  }
  async purge(message) {
    if (!confirm(app.translator.trans("ramon-chat.forum.message.purge_confirm", {}, true))) {
      return;
    }
    try {
      await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-messages/").concat(message.id(), "/purge")
      });
      this.attrs.state.removeMessage(Number(message.channelId()), Number(message.id()));
    } catch (_unused) {
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.message.purge_failed"));
    } finally {
      m.redraw();
    }
  }
  content(message) {
    const html = message.contentHtml();
    return m("div", {
      className: "ChatMessage-content"
    }, this.announcementIcon(message), html ? m.trust(html) : message.content(), message.isEdited() ? this.editedMark(message) : null);
  }

  /**
   * Renders the "edited" marker. `editedAt` can be absent on a row the socket
   * patched before the API answered, so the bare word is the fallback rather
   * than a marker that says "edited Invalid Date".
   */
  editedMark(message) {
    const at = message.editedAt();
    if (!at) {
      return m("span", {
        className: "ChatMessage-editedMark"
      }, "(", app.translator.trans("ramon-chat.forum.message.edited"), ")");
    }
    return m("time", {
      className: "ChatMessage-editedMark",
      datetime: at.toISOString(),
      title: at.toLocaleString()
    }, "(", app.translator.trans("ramon-chat.forum.message.edited_at", {
      ago: humanTimeUtil(at)
    }), ")");
  }
  systemRow(message) {
    var _message$systemData;
    const key = message.systemKey();
    return m("div", {
      className: "ChatMessage ChatMessage--system"
    }, m("div", {
      className: "ChatMessage-gutter"
    }), m("div", {
      className: "ChatMessage-body"
    }, m("div", {
      className: "ChatMessage-tombstone"
    }, key ? app.translator.trans("ramon-chat.forum.message.system.".concat(key), (_message$systemData = message.systemData()) != null ? _message$systemData : {}) : null)));
  }

  /**
   * Opens the image viewer over the page.
   *
   * Mounted on a node appended to the body rather than rendered inside the row:
   * the message stream is `overflow: auto`, and a full-screen overlay inside it
   * would be clipped to the scroller.
   */
  openLightbox(images, index) {
    const mount = document.createElement("div");
    document.body.appendChild(mount);
    const close = () => {
      m.mount(mount, null);
      mount.remove();
    };
    m.mount(mount, {
      view: () => m(ImageLightbox, {
        uploads: images,
        index: Math.max(0, index),
        message: this.attrs.message,
        onClose: close
      })
    });
  }

  /**
   * The author's avatar with their group badges on it.
   *
   * `user.badges()` is core's own list — the same call `PostUser` makes — so a
   * group's icon, colour and tooltip come from wherever the admin set them, and
   * an extension that adds to `User.prototype.badges` shows up here for free
   * rather than having to know the chat exists.
   *
   * The wrapper is only added when there is something to draw: it exists to
   * position the badges over the avatar, and an empty one on every row would put
   * a positioning context in the gutter for nothing.
   */
  avatar(message) {
    // The bot has no account and therefore no groups, so it never has badges.
    const user = message.isBot() ? null : message.user() || null;
    const badges = user ? user.badges().toArray() : [];
    if (!badges.length) return authorAvatar(message);
    return m("span", {
      className: "ChatMessage-avatar"
    }, authorAvatar(message), m("ul", {
      className: "ChatMessage-badges badges badges--packed"
    }, listItems(badges)));
  }
  shortTime(message) {
    const at = message.createdAt();
    if (!at) return null;
    return m("span", null, at.getHours().toString().padStart(2, "0"), ":", at.getMinutes().toString().padStart(2, "0"));
  }

  /**
   * A prévia da mensagem respondida, acima do corpo.
   *
   * Clicar nela leva à original — é o que a citação já sugere, e sem isso o
   * leitor precisa rolar procurando um trecho de 120 caracteres. Quando a
   * original está fora da janela carregada o clique avisa em vez de não fazer
   * nada, para o silêncio não parecer um botão quebrado.
   *
   * O elemento é um `button` e não a `div` de antes: só assim chega pelo teclado
   * e é anunciado como algo acionável. O nome do autor continua sendo um link
   * para o perfil, então o clique nele é deixado passar em vez de virar salto.
   */
  replyPreview(message) {
    const target = message.replyTo();
    if (!target) return null;
    return m("button", {
      type: "button",
      className: "ChatMessage-replyTo",
      title: app.translator.trans("ramon-chat.forum.message.reply_jump", {}, true),
      onclick: e => {
        var _e$target2;
        e.stopPropagation();
        if ((_e$target2 = e.target) != null && _e$target2.closest("a")) return;
        if (!jumpToMessage(target.id(), e.currentTarget)) {
          app.alerts.show({
            type: "error"
          }, app.translator.trans("ramon-chat.forum.message.reply_not_loaded"));
        }
      }
    }, m("i", {
      className: "fas fa-reply",
      "aria-hidden": "true"
    }), m("span", null, authorLink(target)), m("span", {
      className: "ChatMessage-replyTo-content"
    }, messagePreview(target, 120)));
  }
  reactions(message) {
    var _message$reactionSumm;
    const summary = (_message$reactionSumm = message.reactionSummary()) != null ? _message$reactionSumm : {};
    const emojis = Object.keys(summary);
    if (emojis.length === 0) return null;
    return m("div", {
      className: "ChatReactions"
    }, emojis.map(emoji => {
      const entry = summary[emoji];
      return m("button", {
        key: emoji,
        type: "button",
        className: classList("ChatReactions-chip", {
          "ChatReactions-chip--active": entry.reacted
        }),
        disabled: !message.canReact(),
        onclick: e => {
          e.stopPropagation();
          this.react(message, emoji);
        }
      }, emoji === LIKE_REACTION ? m("i", {
        className: "ChatReactions-icon fas fa-thumbs-up",
        "aria-hidden": "true"
      }) : m("span", {
        className: "ChatReactions-emoji"
      }, displayEmoji(emoji)), m("span", null, entry.count));
    }));
  }
  uploads(message) {
    // `hasMany` yields `false` when unloaded, and its entries are optional when a
    // related record has not been pushed to the store yet.
    const related = message.uploads();
    if (!related) return null;
    const uploads = related.filter(upload => Boolean(upload));
    if (uploads.length === 0) return null;

    // The viewer steps between images, so it needs the images alone — a file
    // attachment in the middle would otherwise be a gap in the sequence.
    const images = uploads.filter(upload => upload.isImage());
    return m("div", {
      className: "ChatUploads"
    }, uploads.map((upload, position) => {
      var _upload$fileName, _upload$width, _upload$height;
      return upload.isImage() ?
      // A button, not a link: this opens a viewer over the conversation
      // rather than navigating away. It used to be `target="_blank"`, which
      // threw the reader out to look at a picture and left them to find
      // their way back.
      m("button", {
        type: "button",
        key: upload.id(),
        className: "ChatUploads-imageButton",
        onclick: e => {
          e.stopPropagation();
          this.openLightbox(images, images.indexOf(upload));
        },
        "aria-label": (_upload$fileName = upload.fileName()) != null ? _upload$fileName : ""
      }, m("img", {
        className: "ChatUploads-image",
        src: safeFileUrl(upload.url()),
        alt: upload.fileName()
        // Intrinsic size from the stored dimensions, so the row does not
        // reflow as the image loads.
        ,
        width: (_upload$width = upload.width()) != null ? _upload$width : undefined,
        height: (_upload$height = upload.height()) != null ? _upload$height : undefined,
        loading: "lazy"
      })) : m("a", {
        key: upload.id(),
        className: "ChatUploads-file",
        href: safeFileUrl(upload.url()),
        target: "_blank",
        rel: "noopener noreferrer"
      }, m("i", {
        className: "fas fa-paperclip",
        "aria-hidden": "true"
      }), m("span", {
        className: "ChatUploads-file-name"
      }, upload.fileName()), m("span", {
        className: "ChatUploads-file-size"
      }, upload.humanSize()));
    }));
  }
  threadIndicator(message) {
    if (this.attrs.hideThreadIndicator) return null;
    const thread = message.thread();

    // Only the thread's root carries the indicator; replies live inside the panel.
    if (!thread || thread.originalMessageId() !== Number(message.id())) return null;
    const last = thread.lastMessage();
    return m("button", {
      type: "button",
      className: "ChatThreadIndicator",
      onclick: e => {
        var _this$attrs$onOpenThr, _this$attrs2;
        e.stopPropagation();
        (_this$attrs$onOpenThr = (_this$attrs2 = this.attrs).onOpenThread) == null || _this$attrs$onOpenThr.call(_this$attrs2, message);
      }
    }, m("i", {
      className: "fas fa-comments",
      "aria-hidden": "true"
    }), m("span", {
      className: "ChatThreadIndicator-count"
    }, app.translator.trans("ramon-chat.forum.thread.replies", {
      count: thread.repliesCount()
    })), last ? m("span", {
      className: "ChatThreadIndicator-preview"
    }, messagePreview(last, 80)) : null);
  }
  actions(message) {
    const items = [];
    if (message.canReact()) {
      var _message$reactionSumm2;
      const liked = Boolean((_message$reactionSumm2 = message.reactionSummary()) == null || (_message$reactionSumm2 = _message$reactionSumm2[LIKE_REACTION]) == null ? void 0 : _message$reactionSumm2.reacted);
      items.push(m(Button, {
        className: classList("ChatMessage-action", {
          "ChatMessage-action--active": liked
        })
        // Filled while liked, outlined otherwise — the same read as flarum/likes.
        ,
        icon: liked ? "fas fa-thumbs-up" : "far fa-thumbs-up",
        title: app.translator.trans(liked ? "ramon-chat.forum.message.unlike" : "ramon-chat.forum.message.like", {}, true),
        onclick: () => this.react(message, LIKE_REACTION)
      }));
    }
    if (message.canCreateThread()) {
      items.push(m(Button, {
        className: "ChatMessage-action",
        icon: "fas fa-comments",
        title: app.translator.trans("ramon-chat.forum.message.reply_in_thread", {}, true),
        onclick: () => {
          var _this$attrs$onOpenThr2, _this$attrs3;
          return (_this$attrs$onOpenThr2 = (_this$attrs3 = this.attrs).onOpenThread) == null ? void 0 : _this$attrs$onOpenThr2.call(_this$attrs3, message);
        }
      }));
    }
    if (message.canReply()) {
      items.push(m(Button, {
        className: "ChatMessage-action",
        icon: "fas fa-reply",
        title: app.translator.trans("ramon-chat.forum.message.reply", {}, true),
        onclick: () => {
          var _this$attrs$onReply, _this$attrs4;
          return (_this$attrs$onReply = (_this$attrs4 = this.attrs).onReply) == null ? void 0 : _this$attrs$onReply.call(_this$attrs4, message);
        }
      }));
    }
    if (message.canEdit()) {
      items.push(m(Button, {
        className: "ChatMessage-action",
        icon: "fas fa-pencil",
        title: app.translator.trans("ramon-chat.forum.message.edit", {}, true),
        onclick: () => {
          var _this$attrs$onEdit, _this$attrs5;
          return (_this$attrs$onEdit = (_this$attrs5 = this.attrs).onEdit) == null ? void 0 : _this$attrs$onEdit.call(_this$attrs5, message);
        }
      }));
    }
    if (app.session.user) {
      items.push(m(Button, {
        className: "ChatMessage-action",
        icon: message.isBookmarked() ? "fas fa-bookmark" : "far fa-bookmark",
        title: app.translator.trans(message.isBookmarked() ? "ramon-chat.forum.message.remove_bookmark" : "ramon-chat.forum.message.bookmark", {}, true),
        onclick: () => this.bookmark(message)
      }));
    }

    // Entering selection mode. Offered on every readable row, because quoting a
    // conversation elsewhere is not a moderator-only act — only *moving* is, and
    // that button is gated separately inside the selection bar.
    items.push(m(Button, {
      className: "ChatMessage-action",
      icon: "fas fa-list-check",
      title: app.translator.trans("ramon-chat.forum.message.select", {}, true),
      onclick: () => {
        const state = this.attrs.state;
        state.selecting = true;
        state.selected.add(Number(message.id()));
        m.redraw();
      }
    }));
    if (message.canPin()) {
      const pinned = Boolean(message.isPinned());
      items.push(m(Button, {
        className: classList("ChatMessage-action", {
          "ChatMessage-action--active": pinned
        })
        // The struck-through pin on an already-pinned message: the icon shows
        // what the click does, not what the message currently is. Colour alone
        // said "this is pinned" and left the reader to guess that pressing it
        // again undoes that — and the accent is invisible to anyone who cannot
        // separate it from the resting colour.
        ,
        icon: pinned ? "fas fa-thumbtack-slash" : "fas fa-thumbtack",
        title: app.translator.trans(pinned ? "ramon-chat.forum.message.unpin" : "ramon-chat.forum.message.pin", {}, true),
        onclick: () => this.pin(message)
      }));
    }

    // Reporting. Last in the row and never the default action, because it is the
    // one here that involves another person's time: it opens a queue item a
    // moderator has to read and decide on.
    if (message.canFlag()) {
      const reported = Boolean(message.isFlagged());
      items.push(m(Button, {
        className: classList("ChatMessage-action", {
          "ChatMessage-action--active": reported
        }),
        icon: "fas fa-flag",
        title: app.translator.trans(reported ? "ramon-chat.forum.message.flagged" : "ramon-chat.forum.message.flag", {}, true),
        onclick: () => app.modal.show(FlagMessageModal, {
          message
        })
      }));
    }
    if (message.canDelete()) {
      items.push(m(Button, {
        className: "ChatMessage-action",
        icon: "fas fa-trash",
        title: app.translator.trans("ramon-chat.forum.message.delete", {}, true),
        onclick: () => this.delete(message)
      }));
    }
    if (items.length === 0) return null;
    return m("div", {
      className: "ChatMessage-actions"
    }, items);
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  async react(message, emoji) {
    var _message$reactionSumm3, _summary$emoji;
    // Optimistic toggle: reactions are the highest-frequency interaction and a
    // round-trip before feedback feels broken.
    const summary = _objectSpread({}, (_message$reactionSumm3 = message.reactionSummary()) != null ? _message$reactionSumm3 : {});
    const entry = (_summary$emoji = summary[emoji]) != null ? _summary$emoji : {
      count: 0,
      reacted: false
    };
    summary[emoji] = {
      count: entry.count + (entry.reacted ? -1 : 1),
      reacted: !entry.reacted
    };
    if (summary[emoji].count <= 0) delete summary[emoji];
    message.pushAttributes({
      reactionSummary: summary
    });
    m.redraw();
    try {
      const payload = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-messages/").concat(message.id(), "/react"),
        body: {
          data: {
            attributes: {
              emoji
            }
          }
        }
      });
      app.store.pushPayload(payload);
    } catch (e) {
      // Put the server's version back rather than guessing at a rollback.
      message.pushAttributes({
        reactionSummary: message.reactionSummary()
      });
    } finally {
      m.redraw();
    }
  }
  async bookmark(message) {
    const was = Boolean(message.isBookmarked());
    message.pushAttributes({
      isBookmarked: !was
    });
    m.redraw();
    try {
      await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-messages/").concat(message.id(), "/bookmark"),
        body: {
          data: {
            attributes: {}
          }
        }
      });
    } catch (_unused2) {
      message.pushAttributes({
        isBookmarked: was
      });
    } finally {
      m.redraw();
    }
  }

  /**
   * Toggles the pin.
   *
   * Optimistic like the reaction toggle, and rolled back from the server's own
   * response rather than from a guess — a failed pin must not leave the row
   * claiming to be pinned to the one person who clicked.
   */
  async pin(message) {
    const was = Boolean(message.isPinned());
    message.pushAttributes({
      isPinned: !was
    });
    m.redraw();
    try {
      const payload = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-messages/").concat(message.id(), "/pin"),
        body: {
          data: {
            attributes: {}
          }
        }
      });
      app.store.pushPayload(payload);
    } catch (e) {
      var _e$response$errors$0$, _e$response;
      message.pushAttributes({
        isPinned: was
      });
      app.alerts.show({
        type: "error"
      }, (_e$response$errors$0$ = e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail) != null ? _e$response$errors$0$ : app.translator.trans("ramon-chat.forum.message.pin_failed"));
    } finally {
      m.redraw();
    }
  }
  async delete(message) {
    if (!confirm(app.translator.trans("ramon-chat.forum.message.delete_confirm", {}, true))) return;
    try {
      var _id, _ref, _app$session$user$id, _app$session$user;
      await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-messages/").concat(message.id(), "/delete")
      });
      message.pushAttributes({
        isDeleted: true,
        content: null,
        contentHtml: null,
        // Stated rather than left to the next fetch. Deleting is optimistic, so
        // until then this row renders from whatever the client last set — and a
        // tombstone that guesses is how "removed by a moderator" ended up on
        // messages people had deleted themselves.
        isModeratorDeleted: Number((_id = (_ref = message.user() || null) == null ? void 0 : _ref.id()) != null ? _id : 0) !== Number((_app$session$user$id = (_app$session$user = app.session.user) == null ? void 0 : _app$session$user.id()) != null ? _app$session$user$id : 0)
      });

      // What the optimistic push above cannot state: `canForceDelete` and its
      // siblings are the server's answer for this actor, and deleting is what
      // makes purging possible in the first place — the flag was false when the
      // row was last read and nothing here can honestly flip it.
      //
      // Not covered by the realtime handler that does the same job: the
      // message-changed broadcast excludes the actor, so the one person who just
      // moderated is the one it never reaches. That is why the control appeared
      // only after a reload.
      refreshMessageCapabilities(Number(message.id()));
    } catch (e) {
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.message.delete_failed"));
    } finally {
      m.redraw();
    }
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ChatMessage', ChatMessage);